import { useCallback, useEffect, useRef, useState } from 'react';
import { EDITING } from '@geoic/shared';

export type AutosaveState = {
  /** `idle` — saqlanmagan o'zgarish yo'q. */
  status: 'idle' | 'dirty' | 'saving' | 'saved' | 'error';
  savedAt: Date | null;
  error: string | null;
};

export type UseAutosaveOptions<T> = {
  /** Kuzatiladigan qiymat — o'zgarsa taymer qayta ishga tushadi. */
  value: T;
  /** Qoralamani serverga yozadi. */
  save: (value: T) => Promise<void>;
  /** Yangi yozuvda id hali yo'q — avtosaqlash o'chiriladi. */
  enabled: boolean;
};

/**
 * 30 soniyalik avtosaqlash (TZ 7.2 §7).
 *
 * Nima uchun har o'zgarishda emas, taymer bilan: blok konstruktorida
 * matn terilganda `onChange` sekundiga o'nlab marta ishlaydi. Har biriga
 * so'rov yuborilsa server bekorga yuklanardi va tarmoq sekin bo'lsa
 * so'rovlar bir-birini quvib o'tib, ESKI holat oxirgi bo'lib yozilardi.
 *
 * Taymer har o'zgarishda QAYTA boshlanmaydi: aks holda xodim to'xtovsiz
 * yozayotganda avtosaqlash umuman ishlamasdi. U birinchi o'zgarishdan
 * keyin qo'yiladi va o'z vaqtida bir marta ishlaydi.
 */
export function useAutosave<T>({ value, save, enabled }: UseAutosaveOptions<T>): AutosaveState & {
  /** Qo'lda saqlashdan keyin holatni tozalash uchun. */
  markClean: () => void;
  flush: () => Promise<void>;
} {
  const [status, setStatus] = useState<AutosaveState['status']>('idle');
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Taymer ichida HAR DOIM oxirgi qiymat kerak, lekin qiymat o'zgargani
  // taymerni qayta qurmasligi kerak — shuning uchun ref.
  const latest = useRef(value);
  const saveRef = useRef(save);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const firstRun = useRef(true);

  latest.current = value;
  saveRef.current = save;

  const run = useCallback(async () => {
    timer.current = null;
    setStatus('saving');
    try {
      await saveRef.current(latest.current);
      setSavedAt(new Date());
      setError(null);
      setStatus('saved');
    } catch (err) {
      // Avtosaqlash xatosi ishni TO'XTATMAYDI: xodim yozishda davom etadi,
      // keyingi urinish 30 soniyadan keyin ketadi. Xabar faqat ko'rsatiladi.
      setError(err instanceof Error ? err.message : 'Saqlanmadi');
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    // Birinchi render — hujjat endi yuklandi, o'zgarish yo'q.
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    if (!enabled) return;

    setStatus((prev) => (prev === 'saving' ? prev : 'dirty'));
    timer.current ??= setTimeout(() => void run(), EDITING.AUTOSAVE_INTERVAL_MS);
  }, [value, enabled, run]);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const markClean = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    setStatus('idle');
    setError(null);
  }, []);

  const flush = useCallback(async () => {
    if (!enabled || timer.current === null) return;
    clearTimeout(timer.current);
    await run();
  }, [enabled, run]);

  return { status, savedAt, error, markClean, flush };
}

/** «30 soniya oldin saqlandi» (D4). */
export function autosaveLabel(state: AutosaveState): string {
  switch (state.status) {
    case 'saving':
      return 'Saqlanmoqda…';
    case 'error':
      return state.error ?? 'Qoralama saqlanmadi';
    case 'dirty':
      return 'Saqlanmagan o‘zgarishlar bor';
    case 'saved':
      return state.savedAt ? `Qoralama saqlandi — ${relative(state.savedAt)}` : 'Qoralama saqlandi';
    default:
      return '';
  }
}

function relative(date: Date): string {
  const seconds = Math.max(0, Math.round((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) return `${seconds} soniya oldin`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} daqiqa oldin`;
  return `${Math.round(minutes / 60)} soat oldin`;
}
